/**
 * Loading Spinner Component
 * @author Maheen Nasir
 * @description An optimized loading spinner component with smooth border animation
 */

import { memo, useMemo } from 'react';
import styles from './Spinner.module.css';

interface SpinnerProps {
  /** Whether to show the spinner as an overlay */
  overlay?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Size of the spinner in pixels */
  size?: number;
}

/**
 * Optimized Spinner component for loading states
 * Uses a single element with border animation for better performance
 */
const Spinner = memo(({ overlay = false, className = '', size = 100 }: SpinnerProps) => {
  // Calculate spinner styles based on size
  const spinnerStyle = useMemo(() => {
    // Scale border width based on size (4% of size, minimum 2px)
    const borderWidth = Math.max(2, Math.floor(size * 0.04));
    
    return {
      width: size,
      height: size,
      borderWidth: borderWidth,
      borderTopWidth: borderWidth,
    };
  }, [size]);

  const spinnerContent = (
    <div className={`${styles.spinnerContainer} ${className}`}>
      <div 
        className={styles.spinner} 
        role="progressbar" 
        aria-label="Loading"
        style={spinnerStyle}
      />
    </div>
  );

  if (overlay) {
    return (
      <div className={styles.spinnerOverlay}>
        {spinnerContent}
      </div>
    );
  }

  return spinnerContent;
});

Spinner.displayName = 'Spinner';

export default Spinner; 